/**
 * Field-validation primitives shared by the two module-system edit-mode field builders:
 * `FormFieldBuilder` (detail/create forms) and `TableFormFieldBuilder` (the inline-edit / mapping
 * row editor used by ModuleSubPanel). These three pieces were byte-duplicated in both builders, so a
 * regex tweak previously had to be made in two places (and could drift). They live here now.
 *
 * Scope note: this is ONLY the genuinely-identical surface. The two builders have otherwise drifted
 * (TableFormFieldBuilder folds HTML into TEXT_AREA, folds ENCRYPTED into plaintext, and has no
 * DEPEND_FIELDS / OVERRIDE_FIELDS / EMAIL_WITH_NAME / templated-filter handling). Do NOT pull their
 * divergent per-type rendering in here — reconciling that is a separate, higher-QA effort.
 *
 * The patterns are module-level singletons on purpose: they carry no `g` flag, so `.test()` is
 * stateless and sharing one instance across every field is safe.
 */

// react-hook-form `pattern` rule for an email-address field.
export const EMAIL_VALIDATION_PATTERN = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: 'Invalid email address',
}

// react-hook-form `pattern` rule for a URL field: optional protocol, domain name OR IPv4, optional
// port/path/query/fragment. Outside production we also accept bare `localhost` (dev/stage entry).
export const URL_VALIDATION_PATTERN = {
  value: process.env.REACT_APP_ENVIRONMENT !== 'production'
    ? new RegExp('^(https?:\\/\\/)?' // protocol
      + '(localhost|([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}|(\\d{1,3}\\.){3}\\d{1,3})' // localhost, domain, or IP
      + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
      + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
      + '(\\#[-a-z\\d_]*)?$', 'i')
    : new RegExp('^(https?:\\/\\/)?' // protocol
      + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
      + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
      + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
      + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
      + '(\\#[-a-z\\d_]*)?$', 'i'), // fragment locator
  message: 'Invalid URL',
}

/**
 * Map a react-hook-form field error to the user-facing message both builders render.
 * @param {object} field - the module field (supplies max_length for the maxLength message)
 * @param {object} error - the RHF error object ({ type, message })
 * @returns {string}
 */
export function handleFieldError(field, error) {
  switch (error?.type) {
    case 'required':
      return 'Field is required'
    case 'maxLength':
      return `Field max length is ${field.max_length}`
    default:
      return error?.message || 'Field error'
  }
}
